"use client";

import Link from "next/link";
import {
  type AriaAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

const PREFETCH_DELAY_MS = 150;

type PrefetchOnIntentLinkProperties = {
  href: string;
  children: ReactNode;
  className?: string;
  ariaCurrent?: AriaAttributes["aria-current"];
};

const PrefetchOnIntentLink = ({
  href,
  children,
  className,
  ariaCurrent,
}: PrefetchOnIntentLinkProperties) => {
  const [shouldPrefetch, setShouldPrefetch] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPrefetchTimeout = () => {
    if (!timeoutRef.current) {
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  };

  const enablePrefetch = () => {
    clearPrefetchTimeout();
    setShouldPrefetch(true);
  };

  const handleMouseEnter = () => {
    if (shouldPrefetch || timeoutRef.current) {
      return;
    }

    timeoutRef.current = setTimeout(enablePrefetch, PREFETCH_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearPrefetchTimeout();
  };

  const handleFocus = () => {
    enablePrefetch();
  };

  useEffect(() => {
    return () => {
      if (!timeoutRef.current) {
        return;
      }

      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, []);

  return (
    <Link
      href={href}
      className={className}
      aria-current={ariaCurrent}
      prefetch={shouldPrefetch ? null : false}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
    >
      {children}
    </Link>
  );
};

export default PrefetchOnIntentLink;
